import React, { useEffect, useState } from "react";
import { Row, Col, Form, Card, ListGroup, Badge, Alert, InputGroup, Spinner } from "react-bootstrap";
import { FaUser, FaSearch } from "react-icons/fa";
import { userService } from "../../services/user.service";
import { userPermissionGroupService } from "../../services/userPermissionGroup.service";
import { permissionGroupPermissionService } from "../../services/permissionGroupPermission.service";
import { permissionService } from "../../services/permission.service";
import { permissionGroupService } from "../../services/permissionGroup.service";
import { PERMISSIONS as permissionsList } from "../../config/permissions";

function UserPermissionsLookup({ user }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [groupPermissions, setGroupPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    if (user?.clientId && user?.clientGuid) {
      loadUsers();
    }
  }, [user?.clientId, user?.clientGuid]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(
        (u) =>
          u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        clientId: user.clientId,
        clientGuid: user.clientGuid,
      };
      const response = await userService.get(params);
      // Handle paginated response: response.data.data contains the actual array
      const userData = response.data?.data || response.data || response;
      // Ensure we always have an array
      const usersArray = Array.isArray(userData) ? userData : [];
      setUsers(usersArray);
      setFilteredUsers(usersArray);
      setError(null);
    } catch (err) {
      setError("Failed to load users");
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (selectedUserId) => {
    if (!selectedUserId) {
      setSelectedUser(null);
      setUserGroups([]);
      setGroupPermissions({});
      return;
    }

    try {
      setLoadingPermissions(true);
      const userObj = users.find((u) => u.userId === selectedUserId);
      setSelectedUser(userObj);

      // Get user's group memberships (just the groupIds)
      const groupMembershipsResp = await userPermissionGroupService.getGroupsForUser(selectedUserId);
      const groupMemberships = groupMembershipsResp.data || groupMembershipsResp;

      // Extract groupIds
      const userGroupIds = groupMemberships.map(m => m.groupId);

      // Fetch all groups for this client to get full details
      const allGroupsResp = await permissionGroupService.getAll({
        clientId: user.clientId,
        clientGuid: user.clientGuid
      });
      const allGroups = allGroupsResp.data || allGroupsResp;

      // Filter to just the groups this user is in
      const userGroupsWithDetails = allGroups.filter(g => userGroupIds.includes(g.groupId));
      setUserGroups(userGroupsWithDetails);

      // Get permissions for each group
      const permissionsMap = {};
      for (const group of userGroupsWithDetails) {
        const permsResp = await permissionGroupPermissionService.getPermissionsForGroup(group.groupId);
        const perms = permsResp.data || permsResp;
        permissionsMap[group.groupId] = perms;
      }
      setGroupPermissions(permissionsMap);
      setError(null);
    } catch (err) {
      setError("Failed to load user permissions");
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Get all unique permissions across all groups
  const getAllEffectivePermissions = () => {
    const permissionIds = new Set();
    Object.values(groupPermissions).forEach((perms) => {
      perms.forEach((p) => permissionIds.add(p.permissionId));
    });
    return Array.from(permissionIds);
  };

  const getPermissionDetails = (permissionId) => {
    return permissionsList.find((p) => p.permissionKey === permissionId);
  };

  const groupPermissionsByCategory = () => {
    const effectivePermIds = getAllEffectivePermissions();
    const categorized = {};

    effectivePermIds.forEach((permId) => {
      const details = getPermissionDetails(permId);
      if (details) {
        if (!categorized[details.category]) {
          categorized[details.category] = [];
        }
        categorized[details.category].push(details);
      }
    });

    return categorized;
  };

  // Get which groups grant a specific permission
  const getGroupsGrantingPermission = (permissionId) => {
    const grantingGroups = [];
    userGroups.forEach((group) => {
      const perms = groupPermissions[group.groupId] || [];
      if (perms.some((p) => p.permissionId === permissionId)) {
        grantingGroups.push(group);
      }
    });
    return grantingGroups;
  };

  // Color palette for permission groups
  const groupColors = [
    'primary',    // Blue
    'success',    // Green
    'warning',    // Yellow/Orange
    'danger',     // Red
    'info',       // Light Blue
    'secondary',  // Gray
    'dark',       // Dark Gray
  ];

  // Get consistent color for a group based on its index
  const getGroupColor = (groupId) => {
    const index = userGroups.findIndex(g => g.groupId === groupId);
    return groupColors[index % groupColors.length];
  };

  if (loading) return <div>Loading users...</div>;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={4}>
          <Card>
            <Card.Body>
              <h5 className="mb-3">
                <FaUser className="me-2" />
                Select User
              </h5>

              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Form.Select
                size="lg"
                value={selectedUser?.userId || ""}
                onChange={(e) => handleUserSelect(e.target.value)}
              >
                <option value="">-- Select a user --</option>
                {Array.isArray(filteredUsers) && filteredUsers.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </Form.Select>

              {selectedUser && (
                <div className="mt-3">
                  <h6>Selected User:</h6>
                  <p className="mb-1">
                    <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
                  </p>
                  <p className="text-muted small mb-0">{selectedUser.email}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          {!selectedUser ? (
            <Alert variant="info">
              <FaUser className="me-2" />
              Select a user to see their effective permissions from all their permission groups.
            </Alert>
          ) : loadingPermissions ? (
            <div className="text-center p-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3">Loading permissions...</p>
            </div>
          ) : (
            <>
              <Card className="mb-3">
                <Card.Body>
                  <h5>Group Memberships</h5>
                  {userGroups.length === 0 ? (
                    <p className="text-muted">This user is not in any permission groups.</p>
                  ) : (
                    <ListGroup>
                      {userGroups.map((group) => (
                        <ListGroup.Item key={group.groupId}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Badge bg={getGroupColor(group.groupId)} className="me-2">
                                {group.groupName}
                              </Badge>
                              {group.groupDescription && (
                                <div className="text-muted small mt-1">{group.groupDescription}</div>
                              )}
                            </div>
                            <Badge bg={group.active ? "success" : "secondary"}>
                              {group.active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          {groupPermissions[group.groupId] && (
                            <div className="mt-2">
                              <small className="text-muted">
                                {groupPermissions[group.groupId].length} permission(s) from this group
                              </small>
                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <h5>Effective Permissions</h5>
                  <p className="text-muted small">
                    All unique permissions this user has from their group memberships:
                  </p>

                  {getAllEffectivePermissions().length === 0 ? (
                    <Alert variant="warning">
                      This user has no permissions. Add them to groups with permissions assigned.
                    </Alert>
                  ) : (
                    Object.entries(groupPermissionsByCategory()).map(([category, perms]) => (
                      <div key={category} className="mb-3">
                        <h6 className="text-primary">{category}</h6>
                        <ul className="mb-0">
                          {perms.map((perm) => {
                            const grantingGroups = getGroupsGrantingPermission(perm.permissionKey);
                            return (
                              <li key={perm.permissionKey} className="mb-2">
                                <code>{perm.permissionKey}</code> - {perm.permissionName}
                                {grantingGroups.length > 0 && (
                                  <span className="ms-2">
                                    {grantingGroups.map((group) => (
                                      <Badge
                                        key={group.groupId}
                                        bg={getGroupColor(group.groupId)}
                                        className="me-1"
                                      >
                                        {group.groupName}
                                      </Badge>
                                    ))}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))
                  )}

                  <div className="mt-3 p-3 bg-light rounded">
                    <strong>Total: </strong>
                    {getAllEffectivePermissions().length} unique permission(s)
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </Col>
      </Row>
    </>
  );
}

export default UserPermissionsLookup;
